import { useState, useCallback } from 'react';

/**
 * Generic form manager hook for handling form state and submission.
 * @param {Object} options - Configuration options.
 * @param {Function} options.onSubmit - Async function to call on form submission.
 * @param {Function} options.onSuccess - Callback on successful submission.
 * @param {Function} options.onError - Callback on failed submission.
 * @returns {Object} Form state and handlers.
 */
const useFormManager = ({ onSubmit, onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(0); // 0: initial, 1: success, 2: error

  const handleSubmit = useCallback(async (data) => {
    setIsLoading(true);
    setResponse(0);

    try {
      const result = await onSubmit(data);
      setResponse(1);
      if (onSuccess) onSuccess(result);
    } catch (error) {
      setResponse(2);
      if (onError) onError(error);
    } finally {
      setIsLoading(false);
    }
  }, [onSubmit, onSuccess, onError]);

  const resetResponse = useCallback(() => setResponse(0), []);

  return {
    isLoading,
    response,
    handleSubmit,
    resetResponse,
    setResponse
  };
};

export default useFormManager;
